import * as React from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from './command';

/**
 * Generate time options for a day
 * @param {number} interval - Interval in minutes (default: 15)
 * @returns {Array} Array of time options
 */
function generateTimeOptions(interval = 15) {
	const options = [];
	for (let h = 0; h < 24; h++) {
		for (let m = 0; m < 60; m += interval) {
			const hour24 = h;
			const hour12 = h % 12 || 12;
			const ampm = h < 12 ? 'AM' : 'PM';
			const minutes = m.toString().padStart(2, '0');

			options.push({
				value: `${hour24}:${m}`,
				label: `${hour12}:${minutes} ${ampm}`,
				hour24,
				minute: m,
			});
		}
	}
	return options;
}

/**
 * Parse time string to display label
 */
function getTimeLabel(timeString, options) {
	const option = options.find((opt) => opt.value === timeString);
	return option?.label || timeString;
}

/**
 * TimePicker - A searchable time picker component
 *
 * @param {Object} props
 * @param {string} props.value - Time value in "H:M" format (e.g., "9:0", "14:30")
 * @param {Function} props.onChange - Callback when time changes
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.interval - Time interval in minutes (default: 15)
 * @param {boolean} props.disabled - Disable the component
 */
export function TimePicker({
	value,
	onChange,
	placeholder = 'Select time',
	interval = 15,
	disabled = false,
	className,
}) {
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState('');

	const timeOptions = React.useMemo(() => generateTimeOptions(interval), [interval]);

	const filteredOptions = React.useMemo(() => {
		if (!search) return timeOptions;
		const term = search.toLowerCase();
		return timeOptions.filter((opt) => opt.label.toLowerCase().includes(term));
	}, [timeOptions, search]);

	const handleSelect = (timeValue) => {
		onChange(timeValue);
		setOpen(false);
		setSearch('');
	};

	const displayLabel = value ? getTimeLabel(value, timeOptions) : placeholder;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild disabled={disabled}>
				<button
					type="button"
					role="combobox"
					aria-expanded={open}
					className={cn(
						'flex h-[42px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
						!value && 'text-gray-400',
						className
					)}
					disabled={disabled}
				>
					<div className="flex items-center gap-2">
						<Clock className="h-4 w-4 text-gray-400" />
						<span>{displayLabel}</span>
					</div>
					<ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
				</button>
			</PopoverTrigger>
			<PopoverContent className="w-[200px] p-0" align="start">
				<Command shouldFilter={false}>
					<CommandInput
						placeholder="Search time..."
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList>
						<CommandEmpty>No time found.</CommandEmpty>
						<CommandGroup>
							{filteredOptions.map((option) => (
								<CommandItem
									key={option.value}
									value={option.value}
									onSelect={() => handleSelect(option.value)}
									className={cn(
										'cursor-pointer',
										value === option.value && 'bg-indigo-50 text-indigo-700'
									)}
								>
									{option.label}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}

export default TimePicker;
